const SAFE_ORIGINS = ['https://app.safe.global', 'https://safe.global']

let _isSafeApp: boolean | undefined
let _resolved = false
let _sdkStarted = false
const _listeners = new Set<() => void>()

export function subscribeToSafeDetection(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

/**
 * Detect whether the app is running inside a Gnosis Safe iframe.
 *
 * Uses a layered approach:
 * 1. Not in iframe -> false (definitive)
 * 2. ancestorOrigins (Chrome/Safari/Edge) -> synchronous, definitive
 * 3. document.referrer -> synchronous, best-effort
 * 4. Safe Apps SDK postMessage -> async fallback (Firefox without referrer)
 *
 * Only caches permanently when detection is definitive. When the SDK async
 * fallback is needed, returns false until the SDK resolves, then notifies
 * subscribers so React can re-render.
 */
export function isSafeApp(): boolean {
  if (typeof window === 'undefined') return false
  if (_resolved) return _isSafeApp!

  // Definitely not in an iframe — not a Safe App
  if (!isInIframe()) {
    _isSafeApp = false
    _resolved = true
    return false
  }

  // Fast path: ancestorOrigins (Chrome, Safari, Edge)
  const origins = window.location.ancestorOrigins
  if (origins && origins.length > 0) {
    _isSafeApp = Array.from(origins).some((o) => SAFE_ORIGINS.includes(o))
    _resolved = true
    return _isSafeApp
  }

  // Best-effort: referrer (works when parent sends Referer header)
  if (document.referrer) {
    try {
      if (SAFE_ORIGINS.includes(new URL(document.referrer).origin)) {
        _isSafeApp = true
        _resolved = true
        return true
      }
    } catch {
      // invalid referrer URL — fall through to SDK
    }
  }

  // Inconclusive: in iframe but can't confirm Safe synchronously.
  // Start async SDK detection — returns false until resolved.
  startSdkDetection()
  return false
}

function startSdkDetection(): void {
  if (_sdkStarted) return
  _sdkStarted = true

  void detectWithSdk().then((detected) => {
    _isSafeApp = detected
    _resolved = true
    if (detected) {
      for (const fn of _listeners) fn()
    }
  })
}

async function detectWithSdk(): Promise<boolean> {
  try {
    const { default: SafeAppsSDK } = await import('@safe-global/safe-apps-sdk')
    const sdk = new SafeAppsSDK()
    const result = await Promise.race([
      sdk.safe.getInfo().then(() => true as const),
      new Promise<false>((resolve) => setTimeout(() => resolve(false), 2000)),
    ])
    return result
  } catch {
    return false
  }
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin access throws — we are in an iframe
    return true
  }
}
